import type {
  ModifiedPlayer,
  NFLGameInputs,
  NFLTeamInputs,
  OddsResponse,
  RequestSimulationRunResponse,
  SimulationRun
} from '@library/types/simulations';
import { useEffect, useState } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { GameData } from '@library/types/games';
import { GameInputs } from '@bo-components/simulations/steps/StepOneBuild//GameInputs/GameInputs';
import type { LeagueTeamPlayer } from '@library/types/player';
import { LineAnalyzer } from '@bo-components/simulations/steps/StepTwoResults/LineAnalyzer';
import { MessageBanner } from '@library/components/banners/MessageBanner';
import { NewsInjuryReport } from '@bo-components/simulations/steps/StepOneBuild/NewsInjuryReport';
import type { OverrideValidationError } from '@bo-hooks/simulations/useSimulationDetailSteps';
import { PrimaryButton } from '@library/components/buttons';
import type { SerializedError } from '@reduxjs/toolkit';
import type { SimulationBuilder } from '@bo-hooks/simulations/useSimulationBuilder';
import { SimulationRunNameInput } from '@bo-components/simulations/steps/StepOneBuild/SimulationRunNameInput';
import { StatusExplainer } from '@bo-components/simulations/steps/StepTwoResults/StatusExplainer';
import { TeamDisplay } from '@bo-components/simulations/steps/StepOneBuild/TeamDisplay';
import type { Updater } from 'use-immer';

export interface StepOneBuildProps {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  game: GameData;
  gamePlayers: Array<LeagueTeamPlayer>;
  handleRequestSimulationRun: () => Promise<
    | {
        data: RequestSimulationRunResponse;
      }
    | {
        error: FetchBaseQueryError | SerializedError;
      }
  >;
  leagueId: string;
  overrideValidationErrors: Array<OverrideValidationError>;
  relevantPlayers: Array<LeagueTeamPlayer>;
  setOverrideValidationErrors: Updater<Array<OverrideValidationError>>;
  simulationBuilder: SimulationBuilder;
  simulation?: SimulationRun;
  simulationResults?: boolean;
  simulationOdds?: OddsResponse;
}

export const StepOneBuild = ({
  allMarkets,
  game,
  gamePlayers,
  handleRequestSimulationRun,
  leagueId,
  overrideValidationErrors,
  relevantPlayers,
  setOverrideValidationErrors,
  simulation = undefined,
  simulationBuilder,
  simulationResults = false,
  simulationOdds = undefined
}: StepOneBuildProps) => {
  const {
    scenarioName,
    scenarioNameDefault,
    setScenarioName,
    gameOverrides,
    setGameOverrides,
    homeTeamInputs,
    setHomeTeamInputs,
    awayTeamInputs,
    setAwayTeamInputs,
    modifiedPlayers,
    setModifiedPlayers,
    removedPlayers,
    setRemovedPlayers,
    homeTeamPlayers,
    awayTeamPlayers,
    defaultPlayers
  } = simulationBuilder;

  const [isMultipleStartingQBError, setIsMultipleStartingQBError] =
    useState(false);
  const [
    atLeastOneQBPerTeamSelectedError,
    setAtLeastOneQBPerTeamSelectedError
  ] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const getStartingQBCount = (
      teamModifiedPlayers: Array<ModifiedPlayer>,
      isHomeTeam: boolean
    ) => {
      return teamModifiedPlayers.filter(
        (player) =>
          player.playerOverrides.is_qb &&
          player.homeTeam === isHomeTeam &&
          !removedPlayers.some(
            (removedPlayer) => removedPlayer.playerId === player.playerId
          )
      ).length;
    };

    const numHomeStartingQB = getStartingQBCount(modifiedPlayers, true);
    const numAwayStartingQB = getStartingQBCount(modifiedPlayers, false);

    const tooManyHomeQBError = numHomeStartingQB > 1;
    const tooFewHomeQBError = numHomeStartingQB === 0;
    const tooManyAwayQBError = numAwayStartingQB > 1;
    const tooFewAwayQBError = numAwayStartingQB === 0;

    if (
      tooManyHomeQBError ||
      tooManyAwayQBError ||
      tooFewAwayQBError ||
      tooFewHomeQBError
    ) {
      if (tooManyHomeQBError || tooManyAwayQBError) {
        setErrorMessage('There cannot be more than one default starting QB.');
        setIsMultipleStartingQBError(true);
      } else if (tooFewAwayQBError || tooFewHomeQBError) {
        setErrorMessage('There must be one starting QB for each team.');
        setAtLeastOneQBPerTeamSelectedError(true);
      }
      setOverrideValidationErrors((draft) => {
        if (draft.findIndex((error) => error.field === 'is_qb') === -1) {
          draft.push({
            field: 'is_qb'
          });
        }
      });
    } else {
      setAtLeastOneQBPerTeamSelectedError(false);
      setOverrideValidationErrors((draft) => {
        if (
          draft.findIndex((error) => error.field === 'is_qb') === -1 &&
          !isMultipleStartingQBError
        ) {
          setIsMultipleStartingQBError(false);
        } else if (draft.findIndex((error) => error.field === 'is_qb') !== -1) {
          setIsMultipleStartingQBError(false);
          draft.splice(
            draft.findIndex((error) => error.field === 'is_qb'),
            1
          );
        }
      });
    }
  }, [
    isMultipleStartingQBError,
    modifiedPlayers,
    removedPlayers,
    setOverrideValidationErrors
  ]);

  const handleSetGameOverrides = (
    key: keyof NFLGameInputs,
    value: number | boolean | ''
  ) => {
    setGameOverrides({ ...gameOverrides, [key]: value });
  };

  const handleSetHomeTeamInputs = (
    key: keyof NFLTeamInputs,
    value: number | string | boolean
  ) => {
    setHomeTeamInputs({ ...homeTeamInputs, [key]: value });
  };

  const handleSetAwayTeamInputs = (
    key: keyof NFLTeamInputs,
    value: number | string | boolean
  ) => {
    setAwayTeamInputs({ ...awayTeamInputs, [key]: value });
  };

  const closeErrorMessage = () => {
    setAtLeastOneQBPerTeamSelectedError(false);
    setIsMultipleStartingQBError(false);
  };

  return (
    <div className="flex flex-col gap-4">
      {simulation && <StatusExplainer simulation={simulation} />}

      {simulationResults && simulationOdds && simulation && (
        <LineAnalyzer
          allMarkets={allMarkets}
          game={game}
          gamePlayers={relevantPlayers}
          simulation={simulation}
          simulationOdds={simulationOdds}
        />
      )}

      {(isMultipleStartingQBError || atLeastOneQBPerTeamSelectedError) && (
        <MessageBanner
          code="ERROR"
          message={errorMessage}
          onCancel={closeErrorMessage}
        />
      )}

      <NewsInjuryReport
        game={game}
        gamePlayers={gamePlayers}
        leagueId={leagueId}
      />

      <SimulationRunNameInput
        onValueChange={setScenarioName}
        placeholder={scenarioNameDefault}
        value={scenarioName}
      />

      <GameInputs
        gameOverrides={gameOverrides}
        onSetGameOverrides={handleSetGameOverrides}
      />

      <TeamDisplay
        defaultPlayers={defaultPlayers}
        homeTeam
        leagueId={leagueId}
        modifiedPlayers={modifiedPlayers}
        onSetTeamInputs={handleSetHomeTeamInputs}
        overrideValidationErrors={overrideValidationErrors}
        removedPlayers={removedPlayers}
        setModifiedPlayers={setModifiedPlayers}
        setRemovedPlayers={setRemovedPlayers}
        setOverrideValidationErrors={setOverrideValidationErrors}
        simulationId={simulation?.data.id}
        simulationOdds={simulationOdds}
        teamId={game.home_team_id}
        teamName={game.home_team_name}
        teamPlayers={homeTeamPlayers}
        teamInputs={homeTeamInputs}
      />
      <TeamDisplay
        defaultPlayers={defaultPlayers}
        homeTeam={false}
        leagueId={leagueId}
        modifiedPlayers={modifiedPlayers}
        onSetTeamInputs={handleSetAwayTeamInputs}
        overrideValidationErrors={overrideValidationErrors}
        removedPlayers={removedPlayers}
        setModifiedPlayers={setModifiedPlayers}
        setRemovedPlayers={setRemovedPlayers}
        setOverrideValidationErrors={setOverrideValidationErrors}
        simulationId={simulation?.data.id}
        simulationOdds={simulationOdds}
        teamId={game.away_team_id}
        teamName={game.away_team_name}
        teamPlayers={awayTeamPlayers}
        teamInputs={awayTeamInputs}
      />

      <PrimaryButton
        handleOnClick={handleRequestSimulationRun}
        title="Run Simulation"
        className="sticky bottom-2 w-40 self-center p-1 text-sm font-medium shadow-md"
        disabled={overrideValidationErrors?.length > 0}
      >
        Run New Simulation
      </PrimaryButton>
    </div>
  );
};
